import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useAuth, API, BACKEND_URL } from "@/App";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  ArrowLeft, Mic, MicOff, Radio, Clock,
  Loader2, CircleAlert, Edit2, Zap, Wifi, WifiOff, Copy,
  Globe, HelpCircle, Shield, Sparkles, Check, Circle, User,
  ChevronDown, ChevronUp, ChevronLeft, ChevronRight, BookOpen, PictureInPicture2, Target, X
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { createPortal } from "react-dom";
import SmartCard from "@/components/wingman/SmartCard";
import SourceSelector from "@/components/sources/SourceSelector";
import UsageIndicator from "@/components/UsageIndicator";
import { POLL_INTERVAL_MS, SSE_STALENESS_MS, SSE_STALENESS_CHECK_MS } from "@/config/orchestration";
import usePipWindow, { isPipSupported } from "@/components/pip/usePipWindow";
import PipOverlay from "@/components/pip/PipOverlay";

const URGENCY_ORDER = { immediate: 0, when_ready: 1, background: 2 };

function GoalSectionForm({ goal, setGoal, context, setContext, outcomes, setOutcomes, showContextFields, setShowContextFields, items, generating, generateAgenda, eventId, setActiveSources }) {
  return (
    <div className="space-y-3">
      <div>
        <label className="text-[11px] text-muted-foreground font-medium mb-1.5 block">What do you want to achieve?</label>
        <input
          value={goal}
          onChange={(e) => setGoal(e.target.value)}
          className="surface-input w-full h-9 px-3 rounded-lg text-sm"
          placeholder="e.g. Align on Q2 product priorities"
        />
      </div>
      {!showContextFields ? (
        <button type="button" onClick={() => setShowContextFields(true)} className="flex items-center gap-1.5 text-[11px] text-muted-foreground hover:text-foreground">
          <ChevronDown className="w-3 h-3" /> Add more context
        </button>
      ) : (
        <div className="space-y-3">
          <div>
            <label className="text-[11px] text-muted-foreground font-medium mb-1 block">Additional context</label>
            <textarea value={context} onChange={(e) => setContext(e.target.value)} className="surface-input w-full px-3 py-2 rounded-lg text-xs resize-none" rows={2} placeholder="Background info for a better agenda" />
          </div>
          <div>
            <label className="text-[11px] text-muted-foreground font-medium mb-1 block">Desired outcomes</label>
            <textarea value={outcomes} onChange={(e) => setOutcomes(e.target.value)} className="surface-input w-full px-3 py-2 rounded-lg text-xs resize-none" rows={2} placeholder="What should be decided by the end?" />
          </div>
        </div>
      )}
      {goal.trim() && items.length === 0 && (
        <Button type="button" size="sm" className="gap-2 mt-2" onClick={generateAgenda} disabled={generating}>
          {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
          {generating ? "Generating…" : "Generate agenda"}
        </Button>
      )}
      {items.length > 0 && <p className="text-[11px] text-muted-foreground mt-2">{items.length} agenda item{items.length !== 1 ? "s" : ""} ready</p>}
      <div>
        <SourceSelector eventId={eventId} compact onSelectionChange={setActiveSources} />
      </div>
    </div>
  );
}

export default function LiveMeetingV2() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const [event, setEvent] = useState(null);
  const [agenda, setAgenda] = useState(null);
  const [items, setItems] = useState([]);
  const [meeting, setMeeting] = useState(null);
  const [transcript, setTranscript] = useState([]);
  const [cards, setCards] = useState([]);
  const archivedCards = [];
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [botStatus, setBotStatus] = useState("not_started");
  const [errorMessage, setErrorMessage] = useState(null);
  const [editingUrl, setEditingUrl] = useState(false);
  const [meetingUrl, setMeetingUrl] = useState("");
  const [sseConnected, setSseConnected] = useState(false);
  const cardFilter = "ALL";
  const [userFocus, setUserFocus] = useState("auto");
  const [focusUserSpeaker, setFocusUserSpeaker] = useState(null);
  const [livePartial, setLivePartial] = useState(null);
  const [coverage, setCoverage] = useState({});
  const [transcriptOpen, setTranscriptOpen] = useState(true);
  const refreshCoverageRequested = useRef(false);
  const titleGeneratedRef = useRef(false);
  const wasLiveRef = useRef(false); // true only if bot was live during this session
  const [activeSources, setActiveSources] = useState([]);
  const [overlayBannerDismissed, setOverlayBannerDismissed] = useState(false);
  const [goal, setGoal] = useState("");
  const [context, setContext] = useState("");
  const [outcomes, setOutcomes] = useState("");
  const [showContextFields, setShowContextFields] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  const { isPipOpen, portalRoot, openPip, closePip } = usePipWindow();

  const AGENDA_MIN_W = 220;
  const AGENDA_MAX_W = 520;
  const AGENDA_DEFAULT_W = 320;
  const [agendaWidth, setAgendaWidth] = useState(() => {
    const saved = localStorage.getItem("agenda-pane-width");
    const n = saved ? parseInt(saved, 10) : NaN;
    return Number.isFinite(n) ? Math.min(Math.max(n, AGENDA_MIN_W), AGENDA_MAX_W) : AGENDA_DEFAULT_W;
  });
  const isDraggingAgenda = useRef(false);

  const onAgendaDragStart = useCallback((e) => {
    e.preventDefault();
    isDraggingAgenda.current = true;
    const startX = e.clientX;
    const startW = agendaWidth;

    const onMove = (ev) => {
      const newW = Math.min(Math.max(startW + ev.clientX - startX, AGENDA_MIN_W), AGENDA_MAX_W);
      setAgendaWidth(newW);
    };
    const onUp = () => {
      isDraggingAgenda.current = false;
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      setAgendaWidth((w) => {
        localStorage.setItem("agenda-pane-width", String(w));
        return w;
      });
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  }, [agendaWidth]);

  const pollRef = useRef(null);
  const transcriptEndRef = useRef(null);
  const sseRef = useRef(null);
  const autoJoinAttempted = useRef(false);
  const lastTranscriptAtRef = useRef(0);
  const userManuallyChangedSpeakerRef = useRef(false);
  const [forceReconnect, setForceReconnect] = useState(0);

  useEffect(() => {
    let autoPersistTimeoutId;
    (async () => {
      try {
        const [eventResp, agendaResp, statusResp, transcResp, focusResp, cardsResp, coverageResp, sourceSelResp] =
          await Promise.all([
            axios.get(`${API}/events/${eventId}`, { withCredentials: true }),
            axios.get(`${API}/agendas/${eventId}`, { withCredentials: true }),
            axios.get(`${API}/meetings/${eventId}/status`, { withCredentials: true }),
            axios.get(`${API}/meetings/${eventId}/transcript`, { withCredentials: true }),
            axios.get(`${API}/meetings/${eventId}/focus`, { withCredentials: true }),
            axios.get(`${API}/meetings/${eventId}/cards`, { withCredentials: true }).catch(() => ({ data: [] })),
            axios.get(`${API}/meetings/${eventId}/coverage`, { withCredentials: true }).catch(() => ({ data: { items: [] } })),
            axios.get(`${API}/meetings/${eventId}/source-selection`, { withCredentials: true }).catch(() => ({ data: { sources: [] } })),
          ]);
        setEvent(eventResp.data);
        setMeetingUrl(eventResp.data?.meeting_url || "");
        if (agendaResp.data) {
          setAgenda(agendaResp.data);
          setItems(agendaResp.data.items || []);
          setGoal(agendaResp.data.goal || "");
          setContext(agendaResp.data.context || "");
          setOutcomes(agendaResp.data.desired_outcomes || "");
          setShowContextFields(!!(agendaResp.data.context || agendaResp.data.desired_outcomes));
        }
        setMeeting(statusResp.data);
        setBotStatus(statusResp.data?.status || "not_started");
        if (statusResp.data?.error_message) setErrorMessage(statusResp.data.error_message);
        setTranscript(transcResp.data || []);
        setUserFocus(focusResp.data?.focus || "auto");
        const existingFocus = focusResp.data?.focus_user_speaker;
        const suggested = focusResp.data?.suggested_speaker;
        if (existingFocus) {
          setFocusUserSpeaker(existingFocus);
        } else if (suggested) {
          setFocusUserSpeaker(suggested);
          autoPersistTimeoutId = setTimeout(() => {
            if (userManuallyChangedSpeakerRef.current) return;
            axios.put(
              `${API}/meetings/${eventId}/focus`,
              { focus: focusResp.data?.focus || "auto", focus_user_speaker: suggested },
              { withCredentials: true }
            ).catch(() => {
              if (!userManuallyChangedSpeakerRef.current) {
                toast.error("Failed to save speaker selection");
              }
            });
          }, 400);
        }
        setCards((cardsResp.data || []).map((c) => ({ ...c, pinned: false, used: false })));
        const covItems = coverageResp.data?.items || [];
        const covMap = {};
        covItems.forEach((i) => { covMap[i.item_id] = i; });
        setCoverage(covMap);
        setActiveSources(sourceSelResp.data?.sources || []);
        if (covItems.length === 0 && transcResp.data?.length > 0 && agendaResp.data?.items?.length > 0 && !refreshCoverageRequested.current) {
          refreshCoverageRequested.current = true;
          axios.post(`${API}/meetings/${eventId}/refresh-coverage`, {}, { withCredentials: true })
            .then((r) => {
              const items = r.data?.coverage?.items || [];
              const map = {};
              items.forEach((i) => { map[i.item_id] = i; });
              setCoverage(map);
            })
            .catch(() => {});
        }
      } catch (e) {
        toast.error("Failed to load meeting data");
      } finally {
        setLoading(false);
      }
    })();
    return () => {
      if (autoPersistTimeoutId) clearTimeout(autoPersistTimeoutId);
    };
  }, [eventId]);

  useEffect(() => {
    if (loading || botStatus !== "not_started" || !location.state?.autoJoin || autoJoinAttempted.current) return;
    autoJoinAttempted.current = true;
    startBot();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, botStatus, location.state?.autoJoin]);

  useEffect(() => {
    if (!eventId || !goal.trim()) return;
    const t = setTimeout(async () => {
      try {
        await axios.post(
          `${API}/agendas/${eventId}`,
          { goal: goal.trim(), context: context || null, desired_outcomes: outcomes || null },
          { withCredentials: true }
        );
        setAgenda((prev) => prev ? { ...prev, goal, context, desired_outcomes: outcomes } : prev);
      } catch {
        toast.error("Failed to save goal");
      }
    }, 600);
    return () => clearTimeout(t);
  }, [eventId, goal, context, outcomes]);

  const generateAgenda = useCallback(async () => {
    if (!eventId || !goal.trim()) return;
    setGenerating(true);
    const payload = { goal: goal.trim(), context: context || null, desired_outcomes: outcomes || null };
    try {
      let resp;
      try {
        resp = await axios.post(`${API}/agendas/${eventId}/upsert-and-generate`, payload, { withCredentials: true });
      } catch (e) {
        const isRouteNotFound = e.response?.status === 404 && (e.response?.data?.detail === "Not Found" || !e.response?.data?.detail);
        if (isRouteNotFound) {
          await axios.post(`${API}/agendas/${eventId}`, payload, { withCredentials: true });
          resp = await axios.post(`${API}/agendas/${eventId}/generate`, {}, { withCredentials: true });
        } else {
          throw e;
        }
      }
      const generatedItems = resp.data?.items || [];
      const agendaData = resp.data?.agenda;
      setItems(generatedItems);
      if (agendaData) {
        setAgenda({ ...agendaData, items: generatedItems });
      } else if (generatedItems.length > 0) {
        const agdResp = await axios.get(`${API}/agendas/${eventId}`, { withCredentials: true });
        if (agdResp.data) setAgenda({ ...agdResp.data, items: generatedItems });
      }
      if (generatedItems.length > 0) {
        toast.success(`Generated ${generatedItems.length} agenda items`);
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      const msg = typeof detail === "string"
        ? detail
        : Array.isArray(detail) && detail[0]?.msg
          ? detail.map((e) => e.msg).join("; ")
          : detail?.message || err.message || "Failed to generate agenda";
      toast.error(msg);
    } finally {
      setGenerating(false);
    }
  }, [eventId, goal, context, outcomes]);

  useEffect(() => {
    if (!eventId) return;
    let cancelled = false;

    const connect = () => {
      if (cancelled) return;
      const source = new EventSource(
        `${BACKEND_URL}/api/meetings/${eventId}/stream`,
        { withCredentials: true }
      );

      source.onopen = () => {
        setSseConnected(true);
        lastTranscriptAtRef.current = Date.now();
      };

      source.addEventListener("card", (evt) => {
        try {
          const card = JSON.parse(evt.data);
          setCards((prev) => {
            if (prev.some((c) => c.card_id === card.card_id)) return prev;
            return [{ ...card, pinned: false, used: false }, ...prev];
          });
        } catch (e) {
          console.error("SSE card parse error:", e);
        }
      });

      source.addEventListener("transcript", (evt) => {
        lastTranscriptAtRef.current = Date.now();
        try {
          const seg = JSON.parse(evt.data);
          if (seg.is_final) {
            setTranscript((prev) => {
              const dup = prev.some(
                (p) => p.speaker === seg.speaker && p.text === seg.text
              );
              if (dup) return prev;
              return [...prev, seg];
            });
            setLivePartial(null);
          } else {
            setLivePartial(seg);
          }
        } catch (e) {
          console.error("SSE transcript parse error:", e);
        }
      });

      source.addEventListener("agenda_coverage", (evt) => {
        try {
          const data = JSON.parse(evt.data);
          if (!user?.user_id || data.target_user_id !== user.user_id) return;
          const items = data.items || [];
          setCoverage((prev) => {
            const next = { ...prev };
            items.forEach((i) => {
              const existing = next[i.item_id];
              const covered = (i.covered ?? false) || (existing?.covered ?? false);
              const subPoints = (i.sub_points || []).map((sp, idx) => ({
                ...sp,
                covered: (sp.covered ?? false) || (existing?.sub_points?.[idx]?.covered ?? false),
              }));
              let notes = [...(i.notes || [])];
              if (existing?.notes?.length) {
                const seen = new Set(notes.map((n) => (n || "").toLowerCase()));
                existing.notes.forEach((n) => {
                  if (n && !seen.has((n || "").toLowerCase())) {
                    notes.push(n);
                    seen.add((n || "").toLowerCase());
                  }
                });
              }
              notes = notes.slice(0, 5);
              next[i.item_id] = { ...i, covered, sub_points: subPoints, notes };
            });
            return next;
          });
        } catch (e) {
          console.error("SSE agenda_coverage parse error:", e);
        }
      });

      source.onerror = () => {
        setSseConnected(false);
        source.close();
        if (!cancelled) {
          setTimeout(connect, 3000);
        }
      };

      sseRef.current = source;
    };

    connect();
    return () => {
      cancelled = true;
      sseRef.current?.close();
      sseRef.current = null;
      setSseConnected(false);
    };
  }, [eventId, forceReconnect, user?.user_id]);

  useEffect(() => {
    if (botStatus !== "connected" && botStatus !== "joining") return;
    if (!sseConnected) return;

    const id = setInterval(() => {
      const elapsed = Date.now() - lastTranscriptAtRef.current;
      if (elapsed > SSE_STALENESS_MS) {
        setForceReconnect((r) => r + 1);
      }
    }, SSE_STALENESS_CHECK_MS);

    return () => clearInterval(id);
  }, [botStatus, sseConnected]);

  const pollData = useCallback(async () => {
    try {
      const [transcResp, statusResp] = await Promise.all([
        axios.get(`${API}/meetings/${eventId}/transcript`, { withCredentials: true }),
        axios.get(`${API}/meetings/${eventId}/status`, { withCredentials: true }),
      ]);
      const dbSegments = transcResp.data || [];
      setTranscript((prev) => {
        if (dbSegments.length >= prev.length) return dbSegments;
        return prev;
      });
      const s = statusResp.data?.status || "not_started";
      setBotStatus(s);
      if (statusResp.data?.error_message) setErrorMessage(statusResp.data.error_message);
      if (s === "ended") {
        clearInterval(pollRef.current);
      }
    } catch { /* ignore */ }
  }, [eventId]);

  useEffect(() => {
    if (botStatus === "connected" || botStatus === "joining") {
      pollRef.current = setInterval(pollData, POLL_INTERVAL_MS);
    }
    return () => clearInterval(pollRef.current);
  }, [botStatus, pollData]);

  // Track whether the bot was live during this session (prevents firing on page load of ended meetings)
  useEffect(() => {
    if (botStatus === "joining" || botStatus === "connected") {
      wasLiveRef.current = true;
    }
  }, [botStatus]);

  // Trigger title generation once when meeting ends — only if it ended during this session
  useEffect(() => {
    if (botStatus === "ended" && eventId && !titleGeneratedRef.current && wasLiveRef.current) {
      titleGeneratedRef.current = true;
      axios
        .post(`${API}/meetings/${eventId}/title`, {}, { withCredentials: true })
        .catch(() => {});
    }
  }, [botStatus, eventId]);

  useEffect(() => {
    if (transcriptOpen) {
      transcriptEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [transcript, livePartial, transcriptOpen]);

  useEffect(() => {
    if (focusUserSpeaker) return;
    if (userManuallyChangedSpeakerRef.current) return;
    if (transcript.length === 0) return;

    const userName = (user?.name || "").trim().toLowerCase();
    const emailLocal = ((user?.email || "").split("@")[0] || "").trim().toLowerCase();
    if (!userName && !emailLocal) return;

    const speakers = [...new Set(transcript.map((s) => s.speaker).filter(Boolean))];
    const nameParts = userName.split(/\s+/).filter((p) => p.length >= 2);
    const firstName = nameParts[0] || "";

    for (const speaker of speakers) {
      const sp = speaker.trim().toLowerCase();
      if (!sp || sp.length < 2) continue;
      const spFirst = sp.split(/\s+/)[0] || "";
      if (firstName && (spFirst === firstName || sp.includes(userName) || userName.includes(sp))) {
        setFocusUserSpeaker(speaker);
        axios.put(
          `${API}/meetings/${eventId}/focus`,
          { focus: userFocus, focus_user_speaker: speaker },
          { withCredentials: true }
        ).catch(() => {});
        return;
      }
      if (emailLocal && (sp.includes(emailLocal) || emailLocal.includes(spFirst))) {
        setFocusUserSpeaker(speaker);
        axios.put(
          `${API}/meetings/${eventId}/focus`,
          { focus: userFocus, focus_user_speaker: speaker },
          { withCredentials: true }
        ).catch(() => {});
        return;
      }
    }
  }, [transcript, focusUserSpeaker, user, eventId, userFocus]);

  const changeFocus = async (focus) => {
    setUserFocus(focus);
    try {
      await axios.put(`${API}/meetings/${eventId}/focus`, { focus }, { withCredentials: true });
    } catch { /* non-critical */ }
  };

  const changeFocusUserSpeaker = async (speaker) => {
    userManuallyChangedSpeakerRef.current = true;
    const value = speaker === "" || speaker === "__none__" ? null : speaker;
    setFocusUserSpeaker(value);
    try {
      await axios.put(
        `${API}/meetings/${eventId}/focus`,
        { focus: userFocus, focus_user_speaker: value || "" },
        { withCredentials: true }
      );
    } catch {
      toast.error("Failed to save selection");
    }
  };

  // Cards stay present — no auto-expiry

  const startBot = async () => {
    setStarting(true);
    setErrorMessage(null);
    try {
      const resp = await axios.post(`${API}/meetings/${eventId}/start`, {}, { withCredentials: true });
      setMeeting(resp.data);
      setBotStatus(resp.data.status || "joining");
      toast.success("Wingman joining the meeting...");
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (e.response?.status === 403 && detail?.error === "meeting_limit") {
        toast.error(detail.message, {
          action: { label: "Upgrade", onClick: () => navigate("/settings") },
          duration: 8000,
        });
        setStarting(false);
        return;
      }
      const rawDetailStr = typeof detail === "string" ? detail : JSON.stringify(detail || "");
      const isInvalidMeetingUrl = e.response?.status === 400 && detail?.error === "invalid_meeting_url"
        || (rawDetailStr.includes("meeting_url") && (rawDetailStr.includes("malformed") || rawDetailStr.includes("unsupported platform")));
      if (isInvalidMeetingUrl) {
        const friendlyMsg = "Your meeting link isn't working. Please use a direct join link from Zoom, Google Meet, or Microsoft Teams.";
        setErrorMessage(friendlyMsg);
        setBotStatus("error");
        toast.error(friendlyMsg, { duration: 6000 });
        setEditingUrl(true);
        setStarting(false);
        return;
      }
      const msg = typeof detail === "string" ? detail : detail?.message || "Failed to start";
      setErrorMessage(msg);
      setBotStatus("error");
      toast.error(msg);
    } finally {
      setStarting(false);
    }
  };

  const stopBot = async () => {
    try {
      await axios.post(`${API}/meetings/${eventId}/stop`, {}, { withCredentials: true });
      setBotStatus("ended");
      toast.success("Meeting ended");
    } catch {
      toast.error("Failed to stop meeting");
    }
  };

  const updateMeetingUrl = async () => {
    try {
      await axios.put(`${API}/events/${eventId}/meeting-url`, { meeting_url: meetingUrl }, { withCredentials: true });
      setEditingUrl(false);
      if (event) setEvent({ ...event, meeting_url: meetingUrl });
      toast.success("Meeting URL updated");
    } catch {
      toast.error("Failed to update URL");
    }
  };

  const dismissCard = (cardId) => {
    setCards((prev) => prev.filter((c) => c.card_id !== cardId));
  };

  const pinCard = (cardId) => {
    setCards((prev) =>
      prev.map((c) => (c.card_id === cardId ? { ...c, pinned: !c.pinned } : c))
    );
  };

  const markUsed = (cardId) => {
    setCards((prev) =>
      prev.map((c) => (c.card_id === cardId ? { ...c, used: true } : c))
    );
    toast.success("Noted! We'll learn from this.");
  };

  const myUserId = user?.user_id;
  const coveredCount = items.filter((item) => coverage[item.item_id]?.covered).length;

  const filteredCards = cards
    .filter((c) => {
      if (c.target_user_id != null && c.target_user_id !== myUserId) return false;
      if (cardFilter === "ALL") return true;
      if (cardFilter === "PINNED") return c.pinned;
      if (cardFilter === "ACTIONS") return c.card_type === "follow_up";
      return true;
    })
    .sort((a, b) => {
      if (a.pinned !== b.pinned) return a.pinned ? -1 : 1;
      return (b.timestamp || "").localeCompare(a.timestamp || "");
    });

  const showCards = focusUserSpeaker != null;
  const displayedCards = showCards ? filteredCards : [];

  const groupedTranscript = useMemo(() => {
    const GAP_MS = 10000;
    const groups = [];
    for (const seg of transcript) {
      const last = groups[groups.length - 1];
      const gap = last && seg.timestamp && last.lastTimestamp
        ? new Date(seg.timestamp).getTime() - new Date(last.lastTimestamp).getTime()
        : Infinity;
      if (last && last.speaker === seg.speaker && gap < GAP_MS) {
        last.segments.push(seg);
        last.lastTimestamp = seg.timestamp;
      } else {
        groups.push({
          speaker: seg.speaker,
          segments: [seg],
          firstTimestamp: seg.timestamp,
          lastTimestamp: seg.timestamp,
          id: seg.segment_id,
        });
      }
    }
    return groups;
  }, [transcript]);

  const statusConfig = {
    joining: { color: "bg-amber-500", label: "Joining" },
    connected: { color: "bg-emerald-500", label: "Connected" },
    ended: { color: "bg-muted-foreground/30", label: "Ended" },
    error: { color: "bg-destructive", label: "Error" },
    not_started: { color: "bg-muted-foreground/20", label: "Ready" },
  };

  if (loading) {
    return (
      <div className="min-h-screen mesh-bg flex items-center justify-center">
        <div className="surface-card rounded-2xl p-10 text-center">
          <Loader2 className="w-7 h-7 animate-spin mx-auto mb-4 text-primary" />
          <p className="text-muted-foreground text-sm">Loading wingman...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen mesh-bg flex flex-col overflow-hidden">
      {/* Header */}
      <header className="surface-nav sticky top-0 z-50 px-6 py-2.5">
        <nav className="flex flex-col items-center gap-2 lg:grid lg:grid-cols-[1fr_auto_1fr] lg:items-center lg:gap-4 max-w-[1800px] mx-auto">
          {/* Left section */}
          <div className="flex-shrink-0 flex items-center gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                navigate("/dashboard");
              }}
              className="text-muted-foreground hover:text-foreground h-8 px-2 text-[13px]"
            >
              <ArrowLeft className="w-4 h-4 mr-1.5" />
              Dashboard
            </Button>
            <div className="hidden sm:block">
              <div className="flex items-center gap-2">
                <h1 className="font-heading text-foreground text-lg leading-tight truncate max-w-[300px]">
                  {event?.title || "Meeting"}
                </h1>
                {meetingUrl && (
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard.writeText(meetingUrl);
                      toast.success("Meeting link copied");
                    }}
                    className="p-1 rounded hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <Copy className="w-3.5 h-3.5" />
                  </button>
                )}
              </div>
              <p className="text-[13px] text-muted-foreground truncate max-w-[350px]">
                {agenda?.goal || "No goal set"}
              </p>
              {items.length > 0 && (
                <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground bg-secondary/50 px-2 py-0.5 rounded-full">
                  {coveredCount}/{items.length} topics covered
                </span>
              )}
            </div>
          </div>

          {/* Mode Bar — centered in its grid cell (column 2) */}
          {(botStatus === "connected" || botStatus === "joining") ? (
            <div className="flex justify-center">
              <div className="flex items-center gap-0.5 px-2 py-1 rounded-lg bg-secondary border border-border">
                <span className="text-[11px] font-medium text-muted-foreground/60 uppercase tracking-wider mr-1.5">Mode</span>
                {[
                  { key: "auto", label: "Auto", icon: Sparkles, tip: "Show all card types equally — facts, follow-ups, examples, and defense" },
                  { key: "facts", label: "Verify Facts", icon: Globe, tip: "Prioritize fact-checking — flags claims and answers factual questions" },
                  { key: "follow_ups", label: "Ask Questions", icon: HelpCircle, tip: "Prioritize follow-up question suggestions" },
                  { key: "defense", label: "Handle Pushback", icon: Shield, tip: "Prioritize defense cards when someone challenges your position" },
                ].map((f) => {
                  const FIcon = f.icon;
                  return (
                    <button
                      key={f.key}
                      onClick={() => changeFocus(f.key)}
                      title={f.tip}
                      className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-[13px] font-medium transition-all duration-150 ${
                        userFocus === f.key
                          ? "bg-primary text-primary-foreground shadow-sm"
                          : "text-muted-foreground hover:bg-white hover:text-foreground"
                      }`}
                    >
                      <FIcon className="w-3.5 h-3.5" />
                      {f.label}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="hidden lg:block" />
          )}

          {/* Right buttons — centered on narrow, right-aligned on lg+ (column 3) */}
          <div className="flex-shrink-0 flex items-center justify-center gap-2.5 lg:justify-end">
            <UsageIndicator />
            {isPipSupported() && (
              <button
                onClick={() => {
                  if (isPipOpen) { closePip(); return; }
                  openPip({ width: 380, height: 540 }).catch((err) => {
                    toast.error(err?.message || "Could not open overlay. Try a supported browser.");
                  });
                }}
                className={`inline-flex items-center gap-1.5 text-xs h-7 px-2.5 rounded-md border transition-colors ${
                  isPipOpen
                    ? "border-emerald-300 bg-emerald-500/10 text-emerald-700"
                    : "border-border text-muted-foreground hover:text-foreground hover:bg-secondary/50"
                }`}
                title={isPipOpen ? "Close overlay" : "Open overlay"}
              >
                <PictureInPicture2 className="w-3 h-3" />
                {isPipOpen ? "Close Overlay" : "Overlay"}
              </button>
            )}
            <Badge variant="outline" className="gap-1.5 text-xs h-7">
              <span className={`w-1.5 h-1.5 rounded-full ${statusConfig[botStatus]?.color} ${botStatus === "connected" ? "animate-pulse" : ""}`} />
              {statusConfig[botStatus]?.label}
            </Badge>

            {botStatus === "not_started" || botStatus === "error" ? (
              <Button
                size="sm"
                onClick={startBot}
                disabled={starting}
                className="h-8 text-[13px] rounded-lg px-4"
              >
                {starting ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Zap className="w-4 h-4 mr-1.5" />}
                {botStatus === "error" ? "Retry" : "Join"}
              </Button>
            ) : botStatus === "ended" ? (
              <Button
                size="sm"
                onClick={() => navigate(`/meetings/${eventId}/review`)}
                className="h-8 text-[13px] rounded-lg px-4"
              >
                Summary
              </Button>
            ) : (
              <Button
                size="sm"
                variant="outline"
                onClick={stopBot}
                className="h-8 text-[13px] rounded-lg px-4 border-destructive/20 text-destructive hover:bg-destructive/5"
              >
                <MicOff className="w-4 h-4 mr-1.5" />
                End
              </Button>
            )}
          </div>
        </nav>
      </header>

      {/* Error Banner */}
      <AnimatePresence>
        {errorMessage && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="bg-destructive/5 border-b border-destructive/10 px-6 py-2.5"
          >
            <div className="flex items-center gap-3 max-w-[1800px] mx-auto">
              <CircleAlert className="w-4 h-4 text-destructive shrink-0" />
              <p className="text-sm text-destructive flex-1">{errorMessage}</p>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setEditingUrl(true)}
                className="text-destructive hover:bg-destructive/10 h-7 text-[13px]"
              >
                <Edit2 className="w-3.5 h-3.5 mr-1" />
                Edit URL
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Overlay CTA Banner */}
      <AnimatePresence>
        {isPipSupported() && !isPipOpen && !overlayBannerDismissed &&
          (botStatus === "connected" || botStatus === "joining") && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="bg-primary/[0.04] border-b border-primary/10 px-6 py-2.5"
          >
            <div className="flex items-center gap-3 max-w-[1800px] mx-auto">
              <PictureInPicture2 className="w-4 h-4 text-primary/70 shrink-0" />
              <p className="text-sm text-foreground/70 flex-1">
                For a smoother experience, view insights in the overlay.
              </p>
              <Button
                size="sm"
                onClick={() => {
                  openPip({ width: 380, height: 540 }).catch((err) => {
                    toast.error(err?.message || "Could not open overlay.");
                  });
                }}
                className="h-7 text-xs rounded-lg px-3"
              >
                <PictureInPicture2 className="w-3 h-3 mr-1.5" />
                Open Overlay
              </Button>
              <button
                onClick={() => setOverlayBannerDismissed(true)}
                className="p-1 rounded hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Edit URL Modal */}
      {editingUrl && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/10 backdrop-blur-sm">
          <div className="surface-elevated rounded-2xl p-6 w-full max-w-md mx-4">
            <h3 className="font-heading text-foreground text-lg mb-4">Update Meeting URL</h3>
            <input
              className="surface-input w-full rounded-xl px-4 py-3 text-sm mb-4"
              value={meetingUrl}
              onChange={(e) => setMeetingUrl(e.target.value)}
              placeholder="https://meet.google.com/..."
            />
            <div className="flex gap-2 justify-end">
              <Button variant="ghost" size="sm" onClick={() => setEditingUrl(false)} className="h-9 text-[13px] rounded-lg">Cancel</Button>
              <Button size="sm" onClick={updateMeetingUrl} className="h-9 text-[13px] rounded-lg">Save & Retry</Button>
            </div>
          </div>
        </div>
      )}

      {/* Main 2-Panel Layout */}
      <main className="flex-1 overflow-hidden relative">
        <div className="h-full px-6 pt-5 pb-5 max-w-[1800px] mx-auto flex flex-row gap-6">
          <div className="flex-1 min-w-0 flex flex-col gap-4 h-full overflow-hidden">
          <div className="flex gap-8 flex-1 min-h-0 overflow-hidden">

            {/* LEFT: Agenda Rail */}
            {items.length > 0 && (
              <div className="shrink-0 flex flex-col overflow-hidden min-h-0 relative" style={{ width: agendaWidth }}>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2.5">
                    <Clock className="w-4 h-4 text-muted-foreground" />
                    <h2 className="font-heading text-foreground text-base">Agenda</h2>
                  </div>
                  <div className="flex items-center gap-1.5">
                    {items.map((item) => (
                      <div
                        key={item.item_id}
                        className={`w-2 h-2 rounded-full transition-colors duration-300 ${
                          coverage[item.item_id]?.covered ? "bg-emerald-500" : "bg-border"
                        }`}
                      />
                    ))}
                    <span className="text-xs text-muted-foreground ml-1.5 font-mono tabular-nums">
                      {coveredCount}/{items.length}
                    </span>
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto custom-scrollbar space-y-1">
                  {items.map((item, idx) => {
                    const cov = coverage[item.item_id];
                    const isCovered = cov?.covered ?? false;
                    const subPoints = item.sub_points || [];
                    const covSubPoints = cov?.sub_points || [];
                    const notes = cov?.notes || [];

                    return (
                      <div key={item.item_id} className="px-3 py-2.5 rounded transition-colors">
                        <div className="flex items-center gap-3">
                          {isCovered ? (
                            <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
                              <Check className="w-3.5 h-3.5 text-emerald-600" />
                            </div>
                          ) : (
                            <div className="w-6 h-6 rounded-full bg-border/40 flex items-center justify-center shrink-0">
                              <Circle className="w-3 h-3 text-muted-foreground/40" />
                            </div>
                          )}
                          <span className={`text-sm flex-1 truncate ${isCovered ? "text-muted-foreground" : "text-foreground/80"}`}>
                            {item.title}
                          </span>
                          <span className="text-xs text-muted-foreground/30 font-mono shrink-0">
                            {item.time_alloc_mins}m
                          </span>
                        </div>
                        {subPoints.length > 0 && (
                          <div className="ml-9 mt-2 space-y-1.5">
                            {subPoints.map((sp, spi) => {
                              const spCovered = covSubPoints[spi]?.covered ?? false;
                              return (
                                <div key={spi} className="flex items-start gap-2">
                                  {spCovered ? (
                                    <Check className="w-3.5 h-3.5 text-emerald-500/60 mt-0.5 shrink-0" />
                                  ) : (
                                    <Circle className="w-3.5 h-3.5 text-muted-foreground/20 mt-0.5 shrink-0" />
                                  )}
                                  <span className={`text-[13px] leading-relaxed ${spCovered ? "text-foreground/50" : "text-foreground/55"}`}>
                                    {sp}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        )}
                        {notes.length > 0 && (
                          <div className="ml-9 mt-2.5 pt-2 border-t border-border/30 space-y-1.5">
                            {notes.map((note, ni) => (
                              <div key={ni} className="flex items-start gap-2">
                                <div className="w-1.5 h-1.5 rounded-full bg-emerald-400/60 mt-[7px] shrink-0" />
                                <p className="text-[13px] text-foreground/50 leading-relaxed">{note}</p>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Resize handle */}
            {items.length > 0 && (
              <div
                onMouseDown={onAgendaDragStart}
                className="w-1.5 shrink-0 cursor-col-resize group flex items-center justify-center self-stretch -mx-1"
              >
                <div className="w-[3px] h-12 rounded-full bg-border/40 group-hover:bg-primary/40 group-active:bg-primary/60 transition-colors" />
              </div>
            )}

            {/* RIGHT: Transcript + Cards */}
            <div className="flex-1 min-w-0 flex flex-col overflow-hidden gap-3 min-h-0">
              {/* Speaker selector */}
              {(botStatus === "connected" || botStatus === "joining") && (
                <div className={`shrink-0 flex items-center gap-2 ${!focusUserSpeaker ? "rounded-xl bg-primary/5 px-3 py-2 border border-primary/20" : ""}`}>
                  <User className={`w-4 h-4 shrink-0 ${!focusUserSpeaker ? "text-primary" : "text-muted-foreground"}`} />
                  <span className={`text-[13px] shrink-0 ${!focusUserSpeaker ? "text-primary font-medium" : "text-muted-foreground"}`}>I am</span>
                  <select
                    value={focusUserSpeaker || "__none__"}
                    onChange={(e) => changeFocusUserSpeaker(e.target.value)}
                    className={`rounded-lg border px-2.5 py-1.5 text-[13px] font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20 min-w-[140px] ${
                      !focusUserSpeaker
                        ? "border-primary/30 bg-white text-foreground"
                        : "border-border bg-white text-foreground"
                    }`}
                  >
                    <option value="__none__">Select who you are</option>
                    {[...new Set([...transcript.map((s) => s.speaker).filter(Boolean), focusUserSpeaker].filter(Boolean))].map((sp) => (
                      <option key={sp} value={sp}>{sp}</option>
                    ))}
                  </select>
                  {displayedCards.length > 0 && (
                    <span className="text-xs text-muted-foreground ml-auto shrink-0">
                      {displayedCards.length} card{displayedCards.length !== 1 ? "s" : ""}
                    </span>
                  )}
                </div>
              )}

              {/* Transcript accordion */}
              <div className="shrink-0 rounded-lg border border-border bg-white overflow-hidden">
                <button
                  onClick={() => setTranscriptOpen((v) => !v)}
                  className="w-full flex items-center gap-2.5 px-4 py-2.5 hover:bg-secondary/50 transition-colors"
                >
                  {botStatus === "connected" ? (
                    <div className="flex items-center gap-[2px] h-4">
                      {[0, 1, 2, 3, 4].map((i) => (
                        <div
                          key={i}
                          className="wave-bar w-[2px] bg-emerald-500/70 rounded-full"
                          style={{ animationDelay: `${i * 0.12}s` }}
                        />
                      ))}
                    </div>
                  ) : (
                    <Radio className="w-4 h-4 text-muted-foreground/40" />
                  )}
                  <span className="text-sm font-semibold text-foreground/80">Transcript</span>
                  {botStatus === "connected" && (
                    <span className="flex items-center gap-1.5 bg-emerald-50 px-2 py-0.5 rounded-full">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                      <span className="text-[11px] text-emerald-700 font-medium">Live</span>
                    </span>
                  )}
                  <span className="text-xs text-muted-foreground/50 font-mono tabular-nums">
                    {transcript.length}
                  </span>
                  {!transcriptOpen && groupedTranscript.length > 0 && (
                    <span className="text-sm text-muted-foreground/60 truncate ml-2 flex-1 text-left">
                      {groupedTranscript[groupedTranscript.length - 1]?.segments?.slice(-1)[0]?.text}
                    </span>
                  )}
                  <ChevronUp className={`w-3.5 h-3.5 text-muted-foreground/50 ml-auto shrink-0 transition-transform ${transcriptOpen ? "rotate-180" : ""}`} />
                </button>
                {transcriptOpen && (
                  <div className="border-t border-border/60 h-[180px] overflow-y-auto custom-scrollbar px-4 py-3">
                    {groupedTranscript.length > 0 || livePartial ? (
                      <div className="space-y-2.5">
                        {groupedTranscript.map((group, gi) => {
                          const isUser = focusUserSpeaker && group.speaker === focusUserSpeaker;
                          const isNewest = gi === groupedTranscript.length - 1 && !livePartial;
                          const ts = group.firstTimestamp
                            ? new Date(group.firstTimestamp).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
                            : "";
                          return (
                            <div key={group.id} className={`space-y-0.5 ${isNewest ? "transcript-newest" : ""}`}>
                              <div className="flex items-center gap-2">
                                <span className={`text-xs font-semibold ${isUser ? "text-primary/80" : "text-foreground/75"}`}>
                                  {group.speaker}
                                </span>
                                <span className="text-xs text-muted-foreground/45 font-mono tabular-nums">{ts}</span>
                                {isNewest && (
                                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500/60 shrink-0" />
                                )}
                              </div>
                              {group.segments.map((seg) => (
                                <p key={seg.segment_id} className="text-sm text-foreground/70 leading-[1.6]">
                                  {seg.text}
                                </p>
                              ))}
                            </div>
                          );
                        })}
                        {livePartial && (
                          <div className="space-y-0.5">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-semibold text-foreground/45">{livePartial.speaker}</span>
                              <span className="flex items-center gap-0.5">
                                <span className="w-1 h-1 rounded-full bg-emerald-500 animate-pulse" />
                                <span className="w-1 h-1 rounded-full bg-emerald-500 animate-pulse" style={{ animationDelay: "0.2s" }} />
                                <span className="w-1 h-1 rounded-full bg-emerald-500 animate-pulse" style={{ animationDelay: "0.4s" }} />
                              </span>
                            </div>
                            <p className="text-sm text-muted-foreground/45 italic leading-[1.6]">{livePartial.text}</p>
                          </div>
                        )}
                        <div ref={transcriptEndRef} />
                      </div>
                    ) : (
                      <div className="flex items-center justify-center py-6">
                        <p className="text-sm text-muted-foreground/40">
                          {botStatus === "connected" ? "Waiting for speech..." : "Transcript will appear here"}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Cards */}
              <div className="flex-1 overflow-y-auto custom-scrollbar pr-1 min-h-0">
                <div className="space-y-4">
                  <AnimatePresence mode="popLayout">
                    {displayedCards.map((card) => (
                      <SmartCard
                        key={card.card_id}
                        card={card}
                        onDismiss={dismissCard}
                        onPin={pinCard}
                        onUsed={markUsed}
                      />
                    ))}
                  </AnimatePresence>
                </div>

                {displayedCards.length === 0 && (
                  <div className="flex flex-col items-center justify-center h-full text-center py-24">
                    {(botStatus === "not_started" || botStatus === "error") || ((botStatus === "joining" || botStatus === "connected") && transcript.length === 0 && !livePartial) ? (
                      <>
                        <div className="w-14 h-14 rounded-2xl bg-secondary flex items-center justify-center mb-5">
                          <Mic className="w-6 h-6 text-muted-foreground" />
                        </div>
                        <p className="text-foreground/60 text-lg font-medium mb-1.5">Ready when you are</p>
                        <p className="text-muted-foreground/40 text-sm max-w-[320px] leading-relaxed">
                          Smart cards will appear once the bot joins. Then select who you are above.
                        </p>
                      </>
                    ) : !focusUserSpeaker && (botStatus === "connected" || botStatus === "joining") && (transcript.length > 0 || livePartial) ? (
                      <>
                        <div className="w-14 h-14 rounded-2xl bg-primary/5 flex items-center justify-center mb-5">
                          <User className="w-6 h-6 text-primary" />
                        </div>
                        <p className="text-foreground/60 text-lg font-medium mb-1.5">Who are you in this call?</p>
                        <p className="text-muted-foreground/40 text-sm max-w-[320px] leading-relaxed">
                          Select your name above to enable personalized guidance cards.
                        </p>
                      </>
                    ) : botStatus === "connected" ? (
                      <>
                        <div className="relative mb-10">
                          <div className="w-24 h-24 rounded-full bg-primary/[0.04] breathe" />
                          <div className="w-16 h-16 rounded-full bg-primary/[0.06] breathe absolute top-4 left-4" style={{ animationDelay: "0.5s" }} />
                          <div className="absolute inset-0 flex items-center justify-center">
                            <Zap className="w-7 h-7 text-primary/25" />
                          </div>
                        </div>
                        <p className="text-foreground/60 text-lg font-medium tracking-tight">Wingman is listening</p>
                        <p className="text-muted-foreground/40 text-sm mt-2.5 max-w-[320px] leading-relaxed">
                          Cards will appear as the conversation unfolds — stay focused on your meeting
                        </p>
                      </>
                    ) : botStatus === "joining" ? (
                      <>
                        <Loader2 className="w-7 h-7 text-amber-400 animate-spin mb-5" />
                        <p className="text-foreground/60 text-lg font-medium">Joining meeting...</p>
                      </>
                    ) : botStatus === "ended" ? (
                      <>
                        <div className="w-14 h-14 rounded-2xl bg-secondary flex items-center justify-center mb-5">
                          <Radio className="w-6 h-6 text-muted-foreground" />
                        </div>
                        <p className="text-foreground/60 text-lg font-medium mb-2">Meeting ended</p>
                        <Button
                          size="sm"
                          className="mt-4 h-9 text-[13px] rounded-lg"
                          onClick={() => navigate(`/meetings/${eventId}/review`)}
                        >
                          View Summary
                        </Button>
                      </>
                    ) : null}
                  </div>
                )}
              </div>
            </div>
          </div>
          </div>

          {/* Goal & context — Right sidebar (expand/collapse) */}
          <div className={`shrink-0 flex flex-col overflow-hidden border-l border-border transition-all duration-200 ${sidebarOpen ? "w-72 pl-4" : "w-10"}`}>
            <div className="flex items-center justify-between mb-3">
              {sidebarOpen ? (
                <>
                  <h2 className="font-heading text-foreground text-sm flex items-center gap-2">
                    <Target className="w-4 h-4 text-primary" />
                    Goal & context
                  </h2>
                  <button
                    type="button"
                    onClick={() => setSidebarOpen(false)}
                    className="p-1 rounded hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"
                    title="Hide sidebar"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={() => setSidebarOpen(true)}
                  className="w-full flex items-center justify-center py-2 rounded hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"
                  title="Show goal & context"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
              )}
            </div>
            {sidebarOpen && (
              <div className="flex-1 overflow-y-auto custom-scrollbar">
                <GoalSectionForm goal={goal} setGoal={setGoal} context={context} setContext={setContext} outcomes={outcomes} setOutcomes={setOutcomes} showContextFields={showContextFields} setShowContextFields={setShowContextFields} items={items} generating={generating} generateAgenda={generateAgenda} eventId={eventId} setActiveSources={setActiveSources} />
              </div>
            )}
          </div>
        </div>

      </main>

      {/* PiP overlay portal — rendered directly into the floating PiP window */}
      {isPipOpen && portalRoot && (portalRoot.nodeType === 1 || portalRoot.nodeType === 9) &&
        createPortal(
          <PipOverlay
            items={items}
            coverage={coverage}
            cards={displayedCards}
            meetingTitle={event?.title}
            transcriptSpeaker={livePartial?.speaker || transcript[transcript.length - 1]?.speaker}
            transcriptLine={livePartial?.text || transcript[transcript.length - 1]?.text}
          />,
          portalRoot
        )
      }
    </div>
  );
}
